import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { HiDotsVertical } from 'react-icons/hi';
import { t } from 'i18next';
import moment from 'moment';
import { isAxiosError } from 'axios';
import { useMutation } from '@tanstack/react-query';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    ColumnType,
    RadixButton,
    RadixDialog,
    RadixDropdown,
    RadixInput,
    RadixSelect,
    RadixSpinner,
    RadixTable,
} from '@/components/radix';
import { toast } from 'sonner';
import { formatMobileNumber } from '@/utils/POS/Functions';
import { useDebounce, useMediaQuery } from '@/hooks/shared';
import { useFetchDoctorsList } from '@/hooks/api/doctorPortal';
import type { RootState } from '@/redux/slices/slices.types';
import DoctorCard from './components/DoctorCard';
import ChevronRightIcon from '@/assets/Marketing/ChevronRight.svg';
import { api } from '@/utils/Api/POS';
import type {
    GetApiDoctorsPortalDoctors200DoctorsItem,
    PostApiDoctorsPortalInviteBody,
    GetApiDoctorsPortalDoctorsStatus,
} from '@/shared/api/models';
import { GetApiDoctorsPortalDoctorsStatus as DoctorStatusEnum } from '@/shared/api/models/getApiDoctorsPortalDoctorsStatus';
import { FILTER_ALL_VALUE, type DoctorsListQueryParams } from './components/doctorsList.types';
import {
    buildConnectionStatusOptions,
    getConnectionStatusBadgeStyle,
    getConnectionStatusLabel,
    getStatusDateLabel,
    getStatusDateValue,
} from './components/doctorsList.utils';

const PAGE_SIZE = 25;

const initialQueryParams: DoctorsListQueryParams = {
    search: '',
    page: 1,
    connectionStatusFilter: FILTER_ALL_VALUE,
};

const inviteDoctorEmailSchema = Yup.object({
    email: Yup.string().required(t('Setting.EmailIsRequired')).email(t('Setting.InvalidEmailFormat')),
});

export default function DoctorsList() {
    const location = useLocation();
    const isDesktop = useMediaQuery('(min-width: 960px)');
    const [queryParams, setQueryParams] = useState<DoctorsListQueryParams>(initialQueryParams);
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const debouncedSearch = useDebounce(queryParams.search, 400);

    const {
        doctors,
        currentPage,
        totalPages,
        total,
        loading: isLoading,
        error: doctorsError,
    } = useSelector((state: RootState) => state.doctorsList);

    const fetchParams = useMemo(
        () => ({
            page: queryParams.page,
            limit: PAGE_SIZE,
            keyword: debouncedSearch.trim() || undefined,
            status:
                queryParams.connectionStatusFilter === FILTER_ALL_VALUE
                    ? undefined
                    : (queryParams.connectionStatusFilter as GetApiDoctorsPortalDoctorsStatus),
        }),
        [queryParams.page, queryParams.connectionStatusFilter, debouncedSearch],
    );
    const { refetch } = useFetchDoctorsList(fetchParams);

    useEffect(() => {
        if (location.pathname === '/doctors') {
            refetch();
        }
    }, [location.pathname, refetch]);

    const inviteByEmailMutation = useMutation({
        mutationFn: async (email: string) => {
            const body: PostApiDoctorsPortalInviteBody = { email };
            return toast.promise(api.postApiDoctorsPortalInvite(body), {
                loading: t('Doctors.InvitingDoctor'),
                success: (response) => response?.message || t('Doctors.EmailSent'),
                error: (error) => {
                    if (isAxiosError<{ message?: string }>(error)) {
                        const apiMessage = error.response?.data?.message;
                        if (apiMessage) return apiMessage;
                        if (error.response?.status === 404) return t('Doctors.InviteDoctorNotFound');
                    }
                    return t('Doctors.InviteDoctorFailed');
                },
            });
        },
        onSuccess: () => {
            refetch();
        },
    });

    const inviteByEmailFormik = useFormik({
        initialValues: { email: '' },
        validationSchema: inviteDoctorEmailSchema,
        onSubmit: async (values) => {
            try {
                await inviteByEmailMutation.mutateAsync(values.email.trim());
                setInviteDialogOpen(false);
                inviteByEmailFormik.resetForm();
            } catch (error) {
                if (isAxiosError<{ message?: string }>(error)) {
                    const apiMessage = error.response?.data?.message;
                    if (apiMessage) {
                        inviteByEmailFormik.setFieldError('email', apiMessage);
                    }
                }
            }
        },
    });

    useEffect(() => {
        if (doctorsError) {
            toast.error(t('Doctors.FetchDoctorsFailed'));
        }
    }, [doctorsError]);

    const pagination = {
        currentPage: currentPage ?? 1,
        totalPages: totalPages ?? 0,
        total: total ?? 0,
        hasNextPage: (currentPage ?? 1) < (totalPages ?? 0),
        hasPreviousPage: (currentPage ?? 1) > 1,
        onNextPage: () => setQueryParams((prev) => ({ ...prev, page: prev.page + 1 })),
        onPreviousPage: () => setQueryParams((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) })),
    };

    const removeDoctorMutation = useMutation({
        mutationFn: async (doctorUuid: string) =>
            toast.promise(api.deleteApiDoctorsPortalDoctorsDoctorUuid(doctorUuid), {
                loading: t('Doctors.RemovingDoctor'),
                success: t('Doctors.RemoveDoctorSuccess'),
                error: (error) =>
                    isAxiosError(error) && error.response?.status === 404
                        ? t('Doctors.RemoveDoctorNotFound')
                        : t('Doctors.RemoveDoctorFailed'),
            }),
        onSuccess: () => {
            refetch();
        },
    });

    const handleInviteDoctor = useCallback(
        (email: string) => {
            if (!email) {
                toast.error(t('Doctors.InviteDoctorFailed'));
                return;
            }
            inviteByEmailMutation.mutate(email);
        },
        [inviteByEmailMutation],
    );

    const handleRemoveDoctor = useCallback(
        (doctorUuid: string) => {
            if (!doctorUuid) return;
            removeDoctorMutation.mutate(doctorUuid);
        },
        [removeDoctorMutation],
    );

    const columns = useMemo<ColumnType[]>(
        () => [
            {
                id: 'name',
                name: t('Common.Name'),
                selector: (row) => (
                    <p className="text-text-primary font-medium m-0">
                        {' '}
                        {row.firstName} {row.lastName}
                    </p>
                ),
                sortable: true,
                sortValue: (row) => `${row.firstName} ${row.lastName}`.toLowerCase(),
            },
            {
                id: 'email',
                name: t('Common.Email'),
                selector: (row) => (
                    <a href={`mailto:${row.email}`} className=" m-0 hover:underline text-primary-500">
                        {row.email}
                    </a>
                ),
                sortable: true,
                sortValue: (row) => row.email.toLowerCase(),
            },
            {
                id: 'phone',
                name: t('Common.Phone'),
                selector: (row) => (
                    <a
                        href={`tel:${row.phoneCountryCode || '+45'} ${formatMobileNumber(row.phoneNumber || '')}`}
                        className=" text-text-primary m-0"
                    >
                        {row.phoneCountryCode || '+45'} {formatMobileNumber(row.phoneNumber || '') || '-'}
                    </a>
                ),
            },
            {
                id: 'connectionStatus',
                name: t('Common.Status'),
                selector: (row) => {
                    const status = row.status;
                    const style = getConnectionStatusBadgeStyle(status);
                    return (
                        <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium shrink-0"
                            style={{ color: style.color, backgroundColor: style.bg }}
                        >
                            {getConnectionStatusLabel(status)}
                        </span>
                    );
                },
                sortValue: (row) => row.status ?? '',
            },
            {
                id: 'statusDate',
                name: t('Doctors.StatusDate'),
                selector: (row) => {
                    const doctorRow = row as GetApiDoctorsPortalDoctors200DoctorsItem;
                    const label = getStatusDateLabel(doctorRow.status);
                    const dateValue = getStatusDateValue(doctorRow);
                    if (!label || !dateValue) {
                        return <span className="text-text-secondary text-sm m-0">–</span>;
                    }
                    return (
                        <span className="text-text-primary text-sm m-0">
                            {moment(dateValue).format('DD/MM/YYYY HH:mm')}
                        </span>
                    );
                },
                sortValue: (row) => getStatusDateValue(row as GetApiDoctorsPortalDoctors200DoctorsItem) ?? '',
            },
            {
                id: 'actions',
                name: '',
                width: '130px',
                selector: (row) => {
                    const isLinked = row.status === DoctorStatusEnum.LINKED;
                    const isInviting =
                        !isLinked && inviteByEmailMutation.isPending && inviteByEmailMutation.variables === row.email;
                    const isRemoving =
                        isLinked && removeDoctorMutation.isPending && removeDoctorMutation.variables === row.uuid;
                    const items = isLinked
                        ? [
                              {
                                  label: isRemoving ? (
                                      <span className="flex items-center gap-2">
                                          <RadixSpinner size="sm" variant="primary" />
                                          {t('Doctors.RemovingDoctor')}
                                      </span>
                                  ) : (
                                      t('Doctors.RemoveDoctor')
                                  ),
                                  onClick: () => handleRemoveDoctor(row.uuid),
                                  disabled: isRemoving,
                              },
                          ]
                        : [
                              {
                                  label: isInviting ? (
                                      <span className="flex items-center gap-2">
                                          <RadixSpinner size="sm" variant="primary" />
                                          {t('Doctors.InvitingDoctor')}
                                      </span>
                                  ) : (
                                      t('Doctors.InviteDoctor')
                                  ),
                                  onClick: () => handleInviteDoctor(row.email),
                                  disabled: isInviting,
                              },
                          ];
                    return (
                        <RadixDropdown
                            trigger={
                                <button
                                    type="button"
                                    className="bg-transparent p-[2px] focus:outline-none focus:ring-0 text-text-secondary border-none cursor-pointer rounded-sm w-full mx-auto"
                                    title={t('Customer.Actions')}
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    <HiDotsVertical size={18} />
                                </button>
                            }
                            items={items}
                            align="end"
                        />
                    );
                },
            },
        ],
        [
            handleInviteDoctor,
            handleRemoveDoctor,
            inviteByEmailMutation.isPending,
            inviteByEmailMutation.variables,
            removeDoctorMutation.isPending,
            removeDoctorMutation.variables,
        ],
    );

    const connectionStatusSelectOptions = useMemo(() => buildConnectionStatusOptions(), []);

    const paginationComponent = () => {
        if (!pagination) return null;
        return (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-1 pt-4 pb-0 border-t border-border-default mt-4">
                <div className="text-sm text-text-secondary">
                    {t('Common.Showing')} {doctors.length} {t('Common.of')} {pagination.total} {t('Common.items')}
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-sm text-text-secondary">
                        {t('Common.Page')} {pagination.currentPage} {t('Common.of')} {pagination.totalPages}
                    </span>
                    <RadixButton
                        variant="outline"
                        size="sm"
                        onClick={pagination.onPreviousPage}
                        iconOnly
                        disabled={!pagination.hasPreviousPage || isLoading}
                    >
                        <img src={ChevronRightIcon} alt="Previous" className="w-4 h-4 rotate-[180deg]" />
                    </RadixButton>
                    <RadixButton
                        variant="outline"
                        size="sm"
                        onClick={pagination.onNextPage}
                        iconOnly
                        disabled={!pagination.hasNextPage || isLoading}
                    >
                        <img src={ChevronRightIcon} alt="Next" className="w-4 h-4" />
                    </RadixButton>
                </div>
            </div>
        );
    };

    return (
        <React.Fragment>
            {/* Page header (aligned with Email Campaigns) */}
            <div className="mb-2 md:mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div>
                    <h1 className="text-xl font-semibold text-text-primary m-0 md:mb-1">{t('Setting.Doctors')}</h1>
                    <p className="text-sm text-text-secondary m-0">{t('Doctors.ModuleDescription')}</p>
                </div>
                <RadixButton variant="primary" size="base" onClick={() => setInviteDialogOpen(true)}>
                    {t('Doctors.InviteDoctor')}
                </RadixButton>
            </div>

            <RadixDialog
                open={inviteDialogOpen}
                onOpenChange={(open) => {
                    setInviteDialogOpen(open);
                    if (!open) inviteByEmailFormik.resetForm();
                }}
                title={t('Doctors.InviteDoctorByEmail')}
                footer={
                    <div className="flex items-center justify-end gap-2">
                        <RadixButton
                            variant="outline"
                            size="base"
                            onClick={() => {
                                setInviteDialogOpen(false);
                                inviteByEmailFormik.resetForm();
                            }}
                        >
                            {t('Setting.Cancel')}
                        </RadixButton>
                        <RadixButton
                            variant="primary"
                            size="base"
                            onClick={() => inviteByEmailFormik.handleSubmit()}
                            disabled={inviteByEmailFormik.isSubmitting || inviteByEmailMutation.isPending}
                        >
                            {t('Doctors.InviteButton')}
                        </RadixButton>
                    </div>
                }
            >
                <form onSubmit={inviteByEmailFormik.handleSubmit} className="space-y-4">
                    <div>
                        <RadixInput
                            id="invite-doctor-email"
                            name="email"
                            type="email"
                            label={t('Common.Email')}
                            placeholder={t('Common.Email')}
                            value={inviteByEmailFormik.values.email}
                            onChange={inviteByEmailFormik.handleChange}
                            onBlur={inviteByEmailFormik.handleBlur}
                            error={
                                inviteByEmailFormik.touched.email && inviteByEmailFormik.errors.email
                                    ? inviteByEmailFormik.errors.email
                                    : undefined
                            }
                            autoComplete="email"
                        />
                    </div>
                </form>
            </RadixDialog>

            {isDesktop && (
                <div className="mt-0 p-0">
                    <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-border-default">
                        <h2 className="text-lg font-semibold text-text-primary m-0">{t('Doctors.ListTitle')}</h2>
                        <div className="flex flex-wrap items-center gap-3">
                            <div className="min-w-[160px] max-w-[200px]">
                                <RadixSelect
                                    value={queryParams.connectionStatusFilter}
                                    onValueChange={(value) =>
                                        setQueryParams((prev) => ({ ...prev, connectionStatusFilter: value, page: 1 }))
                                    }
                                    options={connectionStatusSelectOptions}
                                    placeholder={t('Doctors.ConnectionStatusFilter')}
                                />
                            </div>
                            <div className="min-w-[220px] max-w-[280px]">
                                <RadixInput
                                    placeholder={t('Doctors.SearchPlaceholder')}
                                    value={queryParams.search}
                                    onChange={(event) =>
                                        setQueryParams((prev) => ({ ...prev, search: event.target.value, page: 1 }))
                                    }
                                />
                            </div>
                        </div>
                    </div>
                    <div>
                        <RadixTable columns={columns} data={doctors} loading={isLoading} />
                        {pagination && pagination.totalPages > 1 && paginationComponent()}
                    </div>
                </div>
            )}

            {/* Mobile: unchanged layout */}
            {!isDesktop && (
                <section className="space-y-4">
                    <div className="flex flex-col gap-3">
                        <RadixSelect
                            value={queryParams.connectionStatusFilter}
                            onValueChange={(value) =>
                                setQueryParams((prev) => ({ ...prev, connectionStatusFilter: value, page: 1 }))
                            }
                            options={connectionStatusSelectOptions}
                            placeholder={t('Doctors.ConnectionStatusFilter')}
                        />
                        <RadixInput
                            placeholder={t('Doctors.SearchPlaceholder')}
                            value={queryParams.search}
                            onChange={(event) =>
                                setQueryParams((prev) => ({ ...prev, search: event.target.value, page: 1 }))
                            }
                        />
                    </div>
                    <div>
                        {isLoading && doctors.length === 0 && (
                            <div className="flex items-center justify-center py-32 border border-border-default border-solid rounded-md">
                                <RadixSpinner size="md" />
                            </div>
                        )}
                        {!isLoading && doctors.length === 0 && (
                            <div className="text-center py-32 border border-border-default border-solid rounded-md">
                                <p className="text-text-secondary m-0">{t('Customer.NoDataFound')}</p>
                            </div>
                        )}
                        {doctors.length > 0 && (
                            <>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {doctors.map((doctor) => (
                                        <DoctorCard
                                            key={doctor.uuid}
                                            doctor={doctor}
                                            onInvite={handleInviteDoctor}
                                            onRemove={handleRemoveDoctor}
                                            isInviting={
                                                inviteByEmailMutation.isPending &&
                                                inviteByEmailMutation.variables === doctor.email
                                            }
                                            isRemoving={
                                                removeDoctorMutation.isPending &&
                                                removeDoctorMutation.variables === doctor.uuid
                                            }
                                        />
                                    ))}
                                </div>
                                {pagination && pagination.totalPages > 1 && paginationComponent()}
                            </>
                        )}
                    </div>
                </section>
            )}
        </React.Fragment>
    );
}
